import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { X } from "lucide-react";
import { WHATSAPP_NUMBER_E164 } from "./SocialLinks";

const WhatsappIconLarge = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" className="h-7 w-7 text-white" aria-hidden="true">
    <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/>
  </svg>
);

const isMobileViewport = () => {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(max-width: 767px)").matches;
};

const FloatingWhatsapp = () => {
  const { pathname } = useLocation();
  const [show, setShow] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 240);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close popover when clicking outside or pressing Escape
  useEffect(() => {
    if (!popoverOpen) return;
    const onDocClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setPopoverOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setPopoverOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [popoverOpen]);

  // Hide on the contact page (the hero already has direct contact options)
  if (pathname === "/contact") return null;

  const message = encodeURIComponent("Hi Calmbreeze — I'd like to ask about your services.");
  const href = `https://wa.me/${WHATSAPP_NUMBER_E164}?text=${message}`;

  const handleButtonClick = (e) => {
    // On desktop, open the QR popover instead of navigating away
    if (!isMobileViewport()) {
      e.preventDefault();
      setPopoverOpen((open) => !open);
    }
    // On mobile, fall through and let the anchor open the deep link
  };

  return (
    <div
      ref={wrapperRef}
      className={`fixed bottom-6 right-6 z-40 transition-all duration-500 ${
        show ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3 pointer-events-none"
      }`}
      data-testid="floating-whatsapp-wrapper"
    >
      {/* QR popover — desktop only */}
      {popoverOpen && (
        <div
          className="absolute bottom-20 right-0 w-72 bg-white rounded-2xl shadow-[0_24px_60px_rgba(0,0,0,0.18)] ring-1 ring-black/5 p-5 animate-in fade-in slide-in-from-bottom-2 duration-200"
          data-testid="whatsapp-qr-popover"
          role="dialog"
          aria-label="Chat with Calmbreeze on WhatsApp"
        >
          <button
            type="button"
            onClick={() => setPopoverOpen(false)}
            aria-label="Close"
            data-testid="whatsapp-qr-close"
            className="absolute top-3 right-3 h-7 w-7 inline-flex items-center justify-center rounded-full hover:bg-black/5 text-ink/60 hover:text-ink transition"
          >
            <X className="h-4 w-4" />
          </button>

          <div className="font-mono text-[10px] tracking-overline uppercase text-accent">
            [ WhatsApp ]
          </div>
          <h3 className="font-display font-bold text-[18px] leading-tight tracking-tight mt-1 mb-3">
            Scan to chat
          </h3>

          <div className="flex items-center justify-center bg-paper-warm rounded-xl p-3 mb-3">
            <QRCodeSVG
              value={href}
              size={172}
              level="M"
              fgColor="#0a0a0a"
              bgColor="transparent"
              data-testid="whatsapp-qr-code"
            />
          </div>

          <p className="text-[12px] text-ink/70 leading-relaxed mb-3">
            Point your phone camera at the code, or open WhatsApp Web in your browser.
          </p>

          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="whatsapp-qr-web-link"
            className="block w-full text-center py-2.5 rounded-full bg-whatsapp hover:brightness-95 text-white text-[12px] font-semibold transition"
          >
            Open WhatsApp Web →
          </a>
        </div>
      )}

      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={handleButtonClick}
        data-testid="floating-whatsapp"
        aria-label="Chat with Calmbreeze on WhatsApp"
        aria-expanded={popoverOpen}
        className="inline-flex items-center gap-3"
      >
        <span className="hidden md:inline-flex items-center px-4 py-2 bg-white border border-black/8 rounded-full shadow-[0_8px_24px_rgba(0,0,0,0.08)] text-[12px] font-semibold text-ink whitespace-nowrap">
          {popoverOpen ? "Close" : "Chat on WhatsApp"}
        </span>
        <span className="relative inline-flex items-center justify-center h-14 w-14 rounded-full bg-whatsapp shadow-[0_12px_32px_rgba(37,211,102,0.45)] hover:scale-105 transition-transform duration-300 animate-wa-pulse">
          <WhatsappIconLarge />
        </span>
      </a>
    </div>
  );
};

export default FloatingWhatsapp;
